<script>
import { mapGetters } from 'vuex';
import router from '../../../../index';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Edicao dos membros do time: 3 seletores separados — SUPERVISORES (minimo 1),
// AGENTES (minimo 1) e OPERACIONAIS (opcional). Membros legados com outros
// papeis (admin) sao preservados no salvamento (otherMemberIds), sem aparecer.
export default {
  components: {
    Spinner,
    PageHeader,
    AgentSelector,
    NextButton,
  },
  validations: {
    selectedSupervisors: {
      isEmpty() {
        return !!this.selectedSupervisors.length;
      },
    },
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedSupervisors: [],
      selectedAgents: [],
      selectedOperacionais: [],
      otherMemberIds: [],
      isCreating: false,
    };
  },

  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      uiFlags: 'teamMembers/getUIFlags',
    }),

    supervisorList() {
      return this.agentList.filter(agent => agent.role === 'supervisor');
    },
    agentOnlyList() {
      return this.agentList.filter(agent => agent.role === 'agent');
    },
    // Operacionais do time (opcional, sem minimo) — pedido do Kairo 02/07/2026.
    operacionalList() {
      return this.agentList.filter(agent => agent.role === 'operacional');
    },
    teamId() {
      return this.$route.params.teamId;
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.TITLE', {
        teamName: this.currentTeam.name,
      });
    },
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    teamMembers() {
      return this.$store.getters['teamMembers/getTeamMembers'](this.teamId);
    },
    showAgentsList() {
      const { id } = this.currentTeam;
      return id && !this.uiFlags.isFetching;
    },
    disableSubmit() {
      return (
        !this.selectedSupervisors.length ||
        !this.selectedAgents.length ||
        this.isCreating
      );
    },
  },

  async mounted() {
    const { teamId } = this.$route.params;
    await this.$store.dispatch('agents/get');
    try {
      await this.$store.dispatch('teamMembers/get', {
        teamId,
      });
      const memberIds = this.teamMembers.map(item => item.id);
      const supervisorIds = this.supervisorList.map(item => item.id);
      const agentIds = this.agentOnlyList.map(item => item.id);
      const operacionalIds = this.operacionalList.map(item => item.id);
      this.selectedSupervisors = memberIds.filter(id =>
        supervisorIds.includes(id)
      );
      this.selectedAgents = memberIds.filter(id => agentIds.includes(id));
      this.selectedOperacionais = memberIds.filter(id =>
        operacionalIds.includes(id)
      );
      this.otherMemberIds = memberIds.filter(
        id =>
          !supervisorIds.includes(id) &&
          !agentIds.includes(id) &&
          !operacionalIds.includes(id)
      );
    } catch {
      this.selectedSupervisors = [];
      this.selectedAgents = [];
      this.selectedOperacionais = [];
      this.otherMemberIds = [];
    }
  },

  methods: {
    updateSelectedSupervisors(newList) {
      this.v$.selectedSupervisors.$touch();
      this.selectedSupervisors = [...newList];
    },
    updateSelectedAgents(newAgentList) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...newAgentList];
    },
    updateSelectedOperacionais(newList) {
      this.selectedOperacionais = [...newList];
    },
    async addAgents() {
      this.isCreating = true;
      const { teamId } = this;
      const members = [
        ...new Set([
          ...this.selectedSupervisors,
          ...this.selectedAgents,
          ...this.selectedOperacionais,
          ...this.otherMemberIds,
        ]),
      ];

      try {
        await this.$store.dispatch('teamMembers/update', {
          teamId,
          agentsList: members,
        });
        router.replace({
          name: 'settings_teams_edit_finish',
          params: {
            page: 'edit',
            teamId,
          },
        });
        this.$store.dispatch('teams/get');
      } catch (error) {
        useAlert(error.message);
      }
      this.isCreating = false;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-8 col-span-6 overflow-auto">
    <form class="flex flex-col gap-4 mx-0" @submit.prevent="addAgents">
      <PageHeader
        :header-title="headerTitle"
        :header-content="$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.DESC')"
      />

      <template v-if="showAgentsList">
        <div class="w-full">
          <h4 class="text-heading-3 text-n-slate-12 mb-2">
            {{ $t('TEAMS_SETTINGS.ADD.SUPERVISORS_LABEL') }}
          </h4>
          <div v-if="v$.selectedSupervisors.$error">
            <p class="error-message pb-2">
              {{ $t('TEAMS_SETTINGS.ADD.SUPERVISOR_VALIDATION_ERROR') }}
            </p>
          </div>
          <AgentSelector
            :agent-list="supervisorList"
            :selected-agents="selectedSupervisors"
            :update-selected-agents="updateSelectedSupervisors"
            :is-working="isCreating"
            :show-submit-button="false"
          />
        </div>

        <div class="w-full">
          <h4 class="text-heading-3 text-n-slate-12 mb-2">
            {{ $t('TEAMS_SETTINGS.ADD.AGENTS_LABEL') }}
          </h4>
          <div v-if="v$.selectedAgents.$error">
            <p class="error-message pb-2">
              {{ $t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
            </p>
          </div>
          <AgentSelector
            :agent-list="agentOnlyList"
            :selected-agents="selectedAgents"
            :update-selected-agents="updateSelectedAgents"
            :is-working="isCreating"
            :show-submit-button="false"
          />
        </div>

        <div class="w-full">
          <h4 class="text-heading-3 text-n-slate-12 mb-2">
            {{ $t('TEAMS_SETTINGS.ADD.OPERACIONAIS_LABEL') }}
          </h4>
          <AgentSelector
            :agent-list="operacionalList"
            :selected-agents="selectedOperacionais"
            :update-selected-agents="updateSelectedOperacionais"
            :is-working="isCreating"
            :show-submit-button="false"
          />
        </div>

        <div class="flex justify-end w-full">
          <NextButton
            type="submit"
            :label="$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')"
            :disabled="disableSubmit"
            :is-loading="isCreating"
          />
        </div>
      </template>
      <div v-else class="flex items-center justify-center py-6">
        <Spinner class="text-n-blue-11" />
      </div>
    </form>
  </div>
</template>
